import { useQueryClient } from '@tanstack/react-query'
import { Divider, type RefSelectProps } from 'antd'
import { useAtomValue } from 'jotai'
import { forwardRef, useCallback, useMemo, useState, type Ref } from 'react'
import { FaPlus } from 'react-icons/fa'

import { Button, Select } from '@/components/elements'
import { type SingleOrMultipleSelectProps } from '@/components/types'
import { MENU, STATUSES } from '@/constants/common/message.constants'
import { type PackageCategoryEntity } from '@/libs/api'
import { useSelectOnChangeHandler } from '@/libs/hooks'
import { selectedDcIdAtom } from '@/routes/_protected/$tenantKey/-states/atoms/selected-dc-id-atom'

import { usePackageCategoriesSelectQuery } from '../../hooks'
import { packageCategoriesQueryKeys } from '../../query-keys'
import { PackageCategoryCreateModal } from '../PackageCategoryCreateModal'

import { mapToPackageCategoryFieldName } from './map-to-package-category-field-name'

type Props = SingleOrMultipleSelectProps<number, PackageCategoryEntity> & {
  tenantKey: string
  /** 拠点ID指定（指定しない場合は選択された拠点IDを使用して絞り込まれる） */
  dcId?: number
  /** 新規作成ボタンを非表示にするかどうか */
  hideCreateButton?: boolean
  /** 新規作成後のコールバック関数 */
  onCreate?: () => void
}

/**
 * 荷姿を選択するSelect
 */
export const PackageCategorySelect = forwardRef(
  (
    {
      placeholder = STATUSES.UNSELECTED,
      mode,
      onChange,
      onCreate,
      tenantKey,
      dcId,
      value,
      hideCreateButton,
      ...props
    }: Props,
    ref: Ref<RefSelectProps> | undefined
  ) => {
    const [isOpenCreateModal, setIsOpenCreateModal] = useState(false)

    const queryClient = useQueryClient()

    const handleChange = useSelectOnChangeHandler({ mode, onChange })

    const selectedDcId = useAtomValue(selectedDcIdAtom)
    if (dcId === undefined) {
      // 拠点IDが指定されていない場合は、選択された拠点IDを使用して絞り込む
      dcId = selectedDcId
    }

    const handleCreateSuccess = useCallback(
      async (packageCategory: PackageCategoryEntity) => {
        await queryClient.invalidateQueries({
          queryKey: packageCategoriesQueryKeys.select(tenantKey, dcId)
        })
        handleChange(packageCategory.id, packageCategory)
        setIsOpenCreateModal(false)

        if (onCreate) onCreate()
      },
      [queryClient, tenantKey, handleChange, onCreate, dcId]
    )

    const { data, isFetching, isLoading } = usePackageCategoriesSelectQuery(
      tenantKey,
      { dcId }
    )

    const options = useMemo(() => {
      return data
        ?.sort((a, b) => a.code.localeCompare(b.code))
        ?.map((packageCategory) => ({
          ...packageCategory,
          // nameで指定すると利用側で取得できるoption.nameの値が変わるため、labelで指定
          label: mapToPackageCategoryFieldName(packageCategory)
        }))
    }, [data])

    return (
      <>
        <Select
          customRef={ref}
          mode={mode}
          value={isLoading ? null : value}
          placeholder={placeholder}
          options={options}
          loading={isFetching}
          onChange={handleChange}
          {...props}
          dropdownRender={(menu) => (
            <div>
              {menu}
              {!hideCreateButton && (
                <>
                  <Divider className="mx-0 my-2" />
                  <div className="text-center">
                    <Button
                      icon={<FaPlus />}
                      onClick={() => setIsOpenCreateModal(true)}
                    >
                      {MENU.CREATING_PACKAGE_CATEGORY}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        />
        <PackageCategoryCreateModal
          open={isOpenCreateModal}
          tenantKey={tenantKey}
          onSuccess={handleCreateSuccess}
          onClose={() => setIsOpenCreateModal(false)}
        />
      </>
    )
  }
)
